// ===================================================================
// KAIXO - Formato de fecha estilo WhatsApp / mensajería
// ===================================================================
// Convierte un timestamp en una etiqueta corta y útil:
//   - < 60s:           "ahora"
//   - mismo día:       "14:32"
//   - ayer:            "Ayer"
//   - 2-6 días atrás:  nombre del día ("lunes", "martes"...)
//   - >= 7 días atrás: fecha "26/04/2026"
//
// Usa Intl.DateTimeFormat con el idioma del usuario para que los nombres
// de día y formato de fecha respeten la configuración local.
// ===================================================================

const LANG_TO_LOCALE = {
    'español':   'es-ES',
    'inglés':    'en-US',
    'francés':   'fr-FR',
    'alemán':    'de-DE',
    'portugués': 'pt-PT',
    'italiano':  'it-IT',
    'euskera':   'eu',
    'chino':     'zh-CN',
    'japonés':   'ja-JP',
    'árabe':     'ar',
    'hindi':     'hi-IN',
    'ruso':      'ru-RU',
    'coreano':   'ko-KR',
    'turco':     'tr-TR',
    'indonesio': 'id-ID'
};

function toLocale(language) {
    if (!language) return 'es-ES';
    return LANG_TO_LOCALE[language] || language || 'es-ES';
}

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

/**
 * Etiqueta breve para timestamp en lista de chats / separadores de día.
 *
 * @param {Date|string|number} input  Fecha a formatear
 * @param {string} language           Idioma del usuario (KAIXO usa 'español', 'inglés', etc.)
 * @param {Object} [opts]
 * @param {boolean} [opts.alwaysShowTime=false]  Si true, dentro del mismo día siempre devuelve HH:MM (sin "ahora")
 * @param {boolean} [opts.fullForOlder=true]     Si true, fechas >7d devuelven dd/mm/yyyy. Si false, día/mes corto.
 * @param {string} [opts.todayLabel]             Override custom para "Hoy"
 * @param {string} [opts.yesterdayLabel]         Override custom para "Ayer"
 * @param {string} [opts.nowLabel]               Override custom para "ahora"
 */
export function formatRelativeDate(input, language = 'español', opts = {}) {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);

    const locale = toLocale(language);

    // Hace menos de 1 minuto
    if (!opts.alwaysShowTime && diffSec >= 0 && diffSec < 60) {
        return opts.nowLabel || (locale.startsWith('en') ? 'now' : 'ahora');
    }

    const today = startOfDay(now);
    const dayOfInput = startOfDay(d);
    const diffDays = Math.round((today.getTime() - dayOfInput.getTime()) / (24 * 60 * 60 * 1000));

    if (diffDays === 0) {
        // Mismo día: HH:MM
        return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) {
        return opts.yesterdayLabel || yesterdayInLocale(locale);
    }
    if (diffDays >= 2 && diffDays <= 6) {
        // Nombre del día
        try {
            const name = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
            return name.charAt(0).toUpperCase() + name.slice(1);
        } catch {
            return d.toLocaleDateString(locale);
        }
    }
    // Más de 7 días
    if (opts.fullForOlder === false) {
        return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
    }
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Etiqueta de cabecera de grupo de mensajes (separador entre días).
 * Misma lógica pero con "Hoy" en lugar de HH:MM y sin "ahora".
 */
export function formatDayHeader(input, language = 'español') {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '';

    const locale = toLocale(language);
    const today = startOfDay(new Date());
    const dayOfInput = startOfDay(d);
    const diffDays = Math.round((today.getTime() - dayOfInput.getTime()) / (24 * 60 * 60 * 1000));

    if (diffDays === 0) return todayInLocale(locale);
    if (diffDays === 1) return yesterdayInLocale(locale);
    if (diffDays >= 2 && diffDays <= 6) {
        const name = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
        return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function yesterdayInLocale(locale) {
    const map = {
        'es': 'Ayer', 'en': 'Yesterday', 'fr': 'Hier', 'de': 'Gestern',
        'pt': 'Ontem', 'it': 'Ieri', 'eu': 'Atzo', 'zh': '昨天',
        'ja': '昨日', 'ar': 'أمس', 'hi': 'कल', 'ru': 'Вчера',
        'ko': '어제', 'tr': 'Dün', 'id': 'Kemarin'
    };
    const code = String(locale).split('-')[0];
    return map[code] || 'Ayer';
}

function todayInLocale(locale) {
    const map = {
        'es': 'Hoy', 'en': 'Today', 'fr': "Aujourd'hui", 'de': 'Heute',
        'pt': 'Hoje', 'it': 'Oggi', 'eu': 'Gaur', 'zh': '今天',
        'ja': '今日', 'ar': 'اليوم', 'hi': 'आज', 'ru': 'Сегодня',
        'ko': '오늘', 'tr': 'Bugün', 'id': 'Hari ini'
    };
    const code = String(locale).split('-')[0];
    return map[code] || 'Hoy';
}

/**
 * "hace N min/h/d" — para mensajes individuales o tooltips. Distinto de
 * formatRelativeDate, que da etiquetas absolutas (HH:MM, "Ayer", etc.).
 */
export function formatTimeAgo(input, language = 'español') {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '';

    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    const locale = toLocale(language);
    const isEs = locale.startsWith('es');

    if (diffSec < 60)        return isEs ? 'ahora' : 'now';
    if (diffSec < 3600)      return isEs ? `hace ${Math.floor(diffSec / 60)} min` : `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400)     return isEs ? `hace ${Math.floor(diffSec / 3600)} h` : `${Math.floor(diffSec / 3600)} h ago`;
    if (diffSec < 604800)    return isEs ? `hace ${Math.floor(diffSec / 86400)} días` : `${Math.floor(diffSec / 86400)} d ago`;
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Hora corta (HH:MM) — para timestamp de cada burbuja de mensaje.
 */
export function formatHm(input, language = 'español') {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(toLocale(language), { hour: '2-digit', minute: '2-digit' });
}

const exported = { formatRelativeDate, formatDayHeader, formatTimeAgo, formatHm };
export default exported;
